import { PANEL_DETAIL_DEFAULTS } from '../config/panelDetailConfig/defaults';

/**
 * calculateMeterGrid - Sayac sayilarina gore grid hesaplar
 * placementMode'a gore farkli yerlesim stratejileri uygular.
 *
 * Modlar:
 *  - row:    Satir bazli — 3F satirlar, son satir bosluklarina 1F, kalan 1F altta
 *  - column: Sutun bazli — 3F sol sutunlar, 1F sag sutunlar (yan yana)
 *
 * Her iki modda da maxRows hedefine ulasilacak sekilde yerlestirilir.
 *
 * @param {Array} meters - Sayac listesi [{id, phase, label}]
 * @param {object} config - Sayac ve grid konfigurasyonu
 * @returns {object} Grid bilgileri
 */
export function calculateMeterGrid(meters, config = PANEL_DETAIL_DEFAULTS) {
  const placementMode = config.meterCompartment?.placementMode || 'row';
  const count3Phase = meters.filter(m => m.phase === 3).length;
  const count1Phase = meters.filter(m => m.phase === 1).length;

  const { width: w3, height: h3 } = config.meter.phase3;
  const { width: w1, height: h1 } = config.meter.phase1;
  const frameThickness = config.frame.thickness;
  const maxHeight = config.meterCompartment?.maxHeight;
  const maxRows3 = config.meter.phase3.maxRows;
  const maxRows1 = config.meter.phase1.maxRows;

  const base = { count3Phase, count1Phase, totalMeters: count3Phase + count1Phase, placementMode };

  const shift = !!config.meterCompartment?.shiftSinglePhase;

  switch (placementMode) {
    case 'column':
      return _gridColumn(base, w3, h3, w1, h1, maxRows3, maxRows1, frameThickness, maxHeight);
    case 'row':
    default:
      return _gridRow(base, w3, h3, w1, h1, maxRows3, maxRows1, frameThickness, maxHeight, shift);
  }
}

/**
 * Satir bazli yerlesim:
 *  1. 3F sayaclar maxRows3 satirina kadar yerlestirilir (genislik buyur)
 *  2. Son 3F satirindaki bos hucrelere 1F sayaclar konur
 *  3. Kalan 1F sayaclar altta, ayni sutun sayisiyla devam eder
 */
function _gridRow(base, w3, h3, w1, h1, maxRows3, maxRows1, ft, maxH, shift) {
  const { count3Phase, count1Phase } = base;

  // Sadece 1F varsa
  if (count3Phase === 0) {
    if (maxH) maxRows1 = Math.max(1, Math.min(maxRows1, Math.floor(maxH / h1)));
    const cols1 = count1Phase > 0 ? Math.ceil(count1Phase / maxRows1) : 0;
    const rows1 = count1Phase > 0 ? Math.ceil(count1Phase / Math.max(cols1, 1)) : 0;
    return { ...base, cols3: 0, rows3: 0, cols1, rows1, filled1FInRow: 0, remaining1FCount: count1Phase };
  }

  // maxHeight uyumlu iterasyon (cerceve haric — cerceve sonradan eklenir)
  let cols3, rows3, filled1FInRow, remaining1FCount, rows1;
  while (maxRows3 >= 1) {
    cols3 = Math.ceil(count3Phase / maxRows3);
    rows3 = Math.ceil(count3Phase / cols3);

    // shift aciksa: son 3F satirindaki bos hucrelere 1F yerlestir
    if (shift) {
      const usedInLastRow = count3Phase - (rows3 - 1) * cols3;
      const emptySlots = cols3 - usedInLastRow;
      filled1FInRow = Math.min(count1Phase, emptySlots);
    } else {
      filled1FInRow = 0;
    }
    remaining1FCount = count1Phase - filled1FInRow;

    // Kalan 1F altta, 3F sutun sayisi kadar sutunda
    rows1 = remaining1FCount > 0 ? Math.ceil(remaining1FCount / cols3) : 0;

    if (maxH) {
      if (rows3 * h3 + rows1 * h1 <= maxH) break;
      if (maxRows3 <= 1) break;
      maxRows3--;
    } else break;
  }

  const cols1 = remaining1FCount > 0 ? cols3 : 0;
  return { ...base, cols3, rows3, cols1, rows1, filled1FInRow, remaining1FCount };
}

/**
 * Sutun bazli yerlesim:
 *  3F sutunlar solda, 1F sutunlar sagda, yan yana.
 *  Her iki grup kendi maxRows degerine kadar dikeyde dolar.
 */
function _gridColumn(base, w3, h3, w1, h1, maxRows3, maxRows1, ft, maxH) {
  const { count3Phase, count1Phase } = base;
  // maxHeight cerceve haric — cerceve sonradan eklenir
  if (maxH) {
    if (count3Phase > 0) maxRows3 = Math.max(1, Math.min(maxRows3, Math.floor(maxH / h3)));
    if (count1Phase > 0) maxRows1 = Math.max(1, Math.min(maxRows1, Math.floor(maxH / h1)));
  }
  const cols3 = count3Phase > 0 ? Math.ceil(count3Phase / maxRows3) : 0;
  const rows3 = count3Phase > 0 ? Math.ceil(count3Phase / Math.max(cols3, 1)) : 0;
  const cols1 = count1Phase > 0 ? Math.ceil(count1Phase / maxRows1) : 0;
  const rows1 = count1Phase > 0 ? Math.ceil(count1Phase / Math.max(cols1, 1)) : 0;
  return { ...base, cols3, rows3, cols1, rows1 };
}

/**
 * calculateMeterCompartmentSize - Sayac bolmesi boyutlarini hesaplar
 * @param {Array} meters - Sayac listesi
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {object} { width, height, grid }
 */
export function calculateMeterCompartmentSize(meters, config = PANEL_DETAIL_DEFAULTS) {
  const grid = calculateMeterGrid(meters, config);
  const { phase3, phase1 } = config.meter;
  const { thickness: frameThickness } = config.frame;
  const mode = grid.placementMode || 'separate';

  let innerWidth, innerHeight;

  switch (mode) {
    case 'column': {
      // 3F sol, 1F sag — genislikler toplanir, yukseklik en buyuk
      const w3 = grid.cols3 * phase3.width;
      const w1 = grid.cols1 * phase1.width;
      innerWidth = w3 + w1;
      const h3 = grid.rows3 * phase3.height;
      const h1 = grid.rows1 * phase1.height;
      innerHeight = Math.max(h3, h1) || (h3 + h1);
      break;
    }
    case 'row':
    default: {
      // 3F genisligi belirler, 1F altta ayni sutun sayisinda
      innerWidth = grid.cols3 * phase3.width;
      innerHeight = grid.rows3 * phase3.height + grid.rows1 * phase1.height;
      if (grid.count3Phase === 0) {
        innerWidth = grid.cols1 * phase1.width;
        innerHeight = grid.rows1 * phase1.height;
      }
      break;
    }
  }

  const totalWidth = innerWidth + (2 * frameThickness);
  const totalHeight = innerHeight + (2 * frameThickness);

  return {
    width: totalWidth,
    height: totalHeight,
    innerWidth,
    innerHeight,
    grid,
  };
}

/**
 * calculateCompartmentSplit - Sayac bolmesi bolunme ihtiyacini hesaplar
 * maxRows'a dayali kapasite hesabi
 * @param {Array} meters - Sayac listesi
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {object} Bolme bilgileri
 */
export function calculateCompartmentSplit(meters, config = PANEL_DETAIL_DEFAULTS) {
  // Tek bolme varsayilan — topology B/C icin kullanici secimi belirler
  return {
    compartmentCount: 1,
    metersPerCompartment: meters.length,
  };
}

/**
 * calculateBusbarSize - Bara bolmesi boyutlarini hesaplar
 * @param {object} meterSize - Sayac bolmesi boyutlari {width, height}
 * @param {object} distSize - Dagitim bolmesi boyutlari {width, height}
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {object} {width, height}
 */
export function calculateBusbarSize(meterSize, distSize, config = PANEL_DETAIL_DEFAULTS) {
  const { height, widthMode } = config.busbarCompartment;

  let width;
  switch (widthMode) {
    case 'METER_ONLY':
      width = meterSize.width;
      break;
    case 'DIST_ONLY':
      width = distSize.width;
      break;
    case 'BOTH':
    default:
      width = meterSize.width + distSize.width;
      break;
  }

  return { width, height };
}

/**
 * calculateDistributionSize - Dagitim bolmesi boyutlarini hesaplar
 * @param {object} meterSize - Sayac bolmesi boyutlari {width, height}
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {object} {width, height}
 */
export function calculateDistributionSize(meterSize, config = PANEL_DETAIL_DEFAULTS) {
  const { width, heightMode } = config.distributionCompartment;
  const { height: busbarHeight } = config.busbarCompartment;
  const { thickness: frameThickness } = config.frame;

  const distWidth = width + (2 * frameThickness);

  let distHeight;
  switch (heightMode) {
    case 'METER_ONLY':
      distHeight = meterSize.height;
      break;
    case 'METER_PLUS_BUSBAR':
    default:
      distHeight = meterSize.height + busbarHeight;
      break;
  }

  return { width: distWidth, height: distHeight };
}

/**
 * calculateTotalPanelSize - Toplam pano boyutlarini hesaplar
 * @param {Array} meters - Sayac listesi
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @returns {object} Tum hesaplanan boyutlar
 */
export function calculateTotalPanelSize(meters, config = PANEL_DETAIL_DEFAULTS) {
  const meterSize = calculateMeterCompartmentSize(meters, config);
  const distSize = calculateDistributionSize(meterSize, config);
  const busbarSize = calculateBusbarSize(meterSize, distSize, config);
  const splitInfo = calculateCompartmentSplit(meters, config);

  // Kapak sayisi hesapla (dikey kapak, genislige gore)
  const doorCount = calculateDoorCount(meterSize.grid, config, meterSize.width);

  // Toplam pano boyutu
  const totalWidth = Math.max(
    meterSize.width + distSize.width,
    busbarSize.width
  );

  const totalHeight = config.distributionCompartment.heightMode === 'METER_PLUS_BUSBAR'
    ? distSize.height
    : meterSize.height + busbarSize.height;

  return {
    meterCompartmentSize: { width: meterSize.width, height: meterSize.height },
    meterGrid: meterSize.grid,
    busbarSize,
    distributionSize: distSize,
    totalSize: { width: totalWidth, height: totalHeight },
    doorCount,
    compartmentCount: splitInfo.compartmentCount,
  };
}

/**
 * calculateDoorCount - Dikey kapak sayisini hesaplar
 * Sayac bolmesi genisligine gore maxDoorWidth'e bagli olarak kapak sayisi belirlenir
 * @param {object} grid - Grid bilgileri
 * @param {object} config - PANEL_DETAIL_DEFAULTS
 * @param {number} meterWidth - Sayac bolmesi toplam genisligi (cm)
 * @returns {number}
 */
export function calculateDoorCount(grid, config = PANEL_DETAIL_DEFAULTS, meterWidth = 0) {
  const maxDoorWidth = config.meterCompartment?.maxDoorWidth || 60;

  if (meterWidth <= 0) return 1;
  if (meterWidth <= maxDoorWidth) return 1;

  return Math.ceil(meterWidth / maxDoorWidth);
}
